/*
 * What the socket is doing, in one pill under the band (VISUAL.md §6.11). It is deliberately not a
 * toast: a connection that is gone stays gone until it comes back, and a message that disappears
 * after six seconds would leave a person typing into a tab that cannot send.
 *
 * Nothing is shown while the socket is open — the normal case must be silent — and nothing is shown
 * for the first eight hundred milliseconds of an outage either. Every cold load begins with
 * `connecting`, so the pill used to flash for 55–90 ms on every single visit; a state that
 * is usually invisible earns the screen by lasting, and once it has the screen it keeps it for a
 * moment after the reconnect, so that a reader is not left wondering what they just saw.
 *
 * Three further decisions are worth knowing. It floats rather than occupying a row, because as a row
 * it moved the rail, the sidebar and the whole message list down 37 px on every hiccup and back up on
 * every reconnect (X-A3 V17, X-A2 P11). It speaks twice: the pill itself is `role="status"`, so
 * the first, polite sentence is read when it appears, and once the outage passes half a minute the
 * announcer says a second one assertively — by then it is no longer a hiccup, and a screen-reader
 * reader has had no way of knowing they were typing into a tab that cannot send (X-A2 A5). The
 * second sentence goes through the one announcer rather than through a live region of this
 * component's own: two assertive regions racing produce interleaved speech (A31).
 *
 * And it names the one outage a reader causes themselves. Twenty connections a minute is what a
 * reload habit reaches (`realtime.connect`), and the pill then said "No connection. Trying again." to
 * somebody whose network was perfectly fine, with no hint that waiting would fix it (X-B1 P13). The
 * socket publishes that reason (`ConnectionDetail`) and the pill counts the wait down, because the
 * number is the only part of this sentence a person can act on.
 */

import { useEffect, useState, useSyncExternalStore } from "react"
import { useDelayedFlag } from "../../lib/motion.ts"
import { t } from "../i18n/index.ts"
import { useNow } from "../i18n/useNow.ts"
import { connectionDetail, watchConnectionDetail } from "../net/socket.ts"
import type { ConnectionState } from "../store/model.ts"
import { useAnnounce } from "./Announcer.tsx"
import { useModel } from "./context.tsx"
import "./feedback.css"

/** How long an outage lasts before it stops being a hiccup and interrupts. */
export const OUTAGE_ALERT_MS = 30_000

/** How long the socket may be away before the pill says so — `--delay-banner`, in script. */
export const BANNER_DELAY_MS = 800

/**
 * And how long it then stays, counted from the moment it appeared. A pill that vanished the
 * millisecond the socket came back would be a flicker with a sentence in it.
 */
export const BANNER_HOLD_MS = 1200

export function ConnectionBanner() {
	const connection = useModel((model) => model.connection)
	const announce = useAnnounce()
	const down = connection !== "open"
	const shown = useDelayedFlag(down, BANNER_DELAY_MS, BANNER_HOLD_MS)

	// The timer starts with the outage and is cleared by the reconnect, so a connection that
	// flickers is never announced at all.
	useEffect(() => {
		if (!down) return
		const timer = setTimeout(
			() => announce("shell.connection.stillOffline", undefined, { assertive: true }),
			OUTAGE_ALERT_MS,
		)
		return () => clearTimeout(timer)
	}, [down, announce])

	if (!shown) return null
	return <Pill connection={connection} />
}

/**
 * The pill itself, and the one thing it remembers: the state it was mounted with.
 *
 * While the socket is down it reports the connection live, because an outage that turns into a
 * revoked session is a different sentence and the reader needs the new one. What it cannot read off
 * the connection is the last moment of its life: during the hold after a reconnect the connection is
 * already `open`, and a pill that changed its sentence on the way out — or lost it — would be
 * reporting a state nobody is in. So the state it came up with stands in for exactly that moment.
 *
 * The second clock (`useNow(1000)`) lives here rather than in the component above it, so that it
 * ticks only while a pill is on screen: a countdown that does not count down is a number that was
 * true once.
 */
function Pill({ connection }: { connection: ConnectionState }) {
	// The pill is mounted only once the outage has lasted, so `connecting` here is a type's fallback
	// rather than a state that happens.
	const [atMount] = useState<Exclude<ConnectionState, "open">>(
		connection === "open" ? "connecting" : connection,
	)
	const reported = connection === "open" ? atMount : connection
	const detail = useSyncExternalStore(watchConnectionDetail, connectionDetail)
	const now = useNow(1000)
	const limited = reported === "offline" && detail.reason === "rateLimited"
	const text = limited
		? t("connection.rateLimited", {
				seconds: Math.max(0, Math.ceil((detail.retryAt - now) / 1000)),
			})
		: reported === "offline"
			? t("shell.connection.offline")
			: t(`connection.${reported}`)

	return (
		<div className={`tb-banner tb-banner--${reported}`} role="status">
			<span className="tb-banner__spinner" aria-hidden="true" />
			{text}
		</div>
	)
}
